import type { DisplayMode } from '@/types/book';

export interface Point {
  x: number;
  y: number;
}

/** 把缩放值限制在 [min, max]。 */
export function clampZoom(zoom: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, zoom));
}

/** 按连续两帧的双指距离比例计算下一缩放倍率。 */
export function scaleZoomByPinchDistance(
  currentZoom: number,
  previousDistance: number,
  currentDistance: number
): number {
  if (previousDistance <= 0) return currentZoom;
  return currentZoom * (currentDistance / previousDistance);
}

/** 书本内容尺寸（双页模式宽度翻倍）。 */
export function computeContentSize(
  pageWidth: number,
  pageHeight: number,
  displayMode: DisplayMode
): { width: number; height: number } {
  return {
    width: pageWidth * (displayMode === 'double' ? 2 : 1),
    height: pageHeight
  };
}

/** 让书本中心落在 stage 原点（视口中心）的 root.position。 */
export function centeredPosition(contentW: number, contentH: number, scale: number): Point {
  return { x: -(contentW * scale) / 2, y: -(contentH * scale) / 2 };
}

/** 缩放时保持可见内容中心不动（含 centerContainer 偏移）。 */
export function scaleAboutVisualCenter(
  position: Point,
  contentW: number,
  contentH: number,
  contentOffsetX: number,
  contentOffsetY: number,
  oldScale: number,
  newScale: number
): Point {
  const ox0 = contentOffsetX * oldScale;
  const oy0 = contentOffsetY * oldScale;
  const visualX = position.x + ox0;
  const visualY = position.y + oy0;
  const cx = visualX + (contentW * oldScale) / 2;
  const cy = visualY + (contentH * oldScale) / 2;
  const newVisualX = cx - (contentW * newScale) / 2;
  const newVisualY = cy - (contentH * newScale) / 2;
  const ox1 = contentOffsetX * newScale;
  const oy1 = contentOffsetY * newScale;
  return { x: newVisualX - ox1, y: newVisualY - oy1 };
}

/** 缩放时保持视口中心下的内容点不动：position 按 newScale/oldScale 等比缩放。 */
export function scaleAboutCenter(position: Point, oldScale: number, newScale: number): Point {
  if (oldScale === 0) return { ...position };
  const ratio = newScale / oldScale;
  return { x: position.x * ratio, y: position.y * ratio };
}

/** 缩放时保持指定 stage 点下的书本内容不动，适用于鼠标滚轮缩放。 */
export function scaleAboutPoint(
  position: Point,
  anchor: Point,
  oldScale: number,
  newScale: number
): Point {
  if (oldScale === 0) return { ...position };
  const ratio = newScale / oldScale;
  return {
    x: anchor.x + (position.x - anchor.x) * ratio,
    y: anchor.y + (position.y - anchor.y) * ratio
  };
}

interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * 单轴回弹：把「书本一条边」贴到「最近的同侧 stage 边」。
 * 之所以按轴（x / y）拆开，是因为书本比 stage 大、且四角都不在 stage 内时，
 * 仍可能有某条边进入了 stage（例如只露出书本顶部，产生上方空白），需要单独回弹该边。
 * 步骤1：书本该轴尺寸 <= stage，直接居中。
 * 步骤2：书本更大时，收集「已进入 stage 区间」的书本起止边，按到同侧 stage 边的直线距离取最近一条贴合。
 * 步骤3：两条边都未进入（书本完全盖住该轴）时保持原位，不回弹。
 */
function snapAxis(
  bookStart: number,
  bookEnd: number,
  stageStart: number,
  stageEnd: number
): number {
  const bookSize = bookEnd - bookStart;
  const stageSize = stageEnd - stageStart;

  // 步骤1：书本更小，居中
  if (bookSize <= stageSize) {
    return stageStart + (stageSize - bookSize) / 2;
  }

  // 步骤2：书本更大，找已进入 stage 的边，贴最近的同侧 stage 边
  const candidates: { distance: number; start: number }[] = [];
  // 起始边（left / top）进入 stage：贴 stage 起始边
  if (bookStart >= stageStart && bookStart <= stageEnd) {
    candidates.push({ distance: Math.abs(bookStart - stageStart), start: stageStart });
  }
  // 结束边（right / bottom）进入 stage：贴 stage 结束边（换算回起点）
  if (bookEnd >= stageStart && bookEnd <= stageEnd) {
    candidates.push({ distance: Math.abs(bookEnd - stageEnd), start: stageEnd - bookSize });
  }

  // 步骤3：无边进入，保持原位
  if (candidates.length === 0) return bookStart;

  return candidates.sort((a, b) => a.distance - b.distance)[0].start;
}

/** 基于矩形相交的回弹：x / y 两轴各自按「边对边、取最近」独立回弹。 */
function snapByRectIntersection(book: Rect, stage: Rect): Point {
  return {
    x: snapAxis(book.left, book.right, stage.left, stage.right),
    y: snapAxis(book.top, book.bottom, stage.top, stage.bottom)
  };
}

/**
 * 当前跨页在 root 局部坐标系下的可见尺寸与 centerContainer 偏移。
 * 首尾单页跨页时宽度为单页，且 offsetX 与 PixiBookRenderer.spreadCenterOffset 一致。
 */
export function computeVisibleBookLayout(
  pageWidth: number,
  pageHeight: number,
  displayMode: DisplayMode,
  currentPageIndex: number,
  pageCount: number
): { width: number; height: number; offsetX: number; offsetY: number } {
  const leftExists = currentPageIndex >= 0 && currentPageIndex < pageCount;
  const rightExists = currentPageIndex + 1 >= 0 && currentPageIndex + 1 < pageCount;
  const singlePageVisible = displayMode === 'single' || !leftExists || !rightExists;

  let offsetX = 0;
  if (displayMode === 'double' && leftExists !== rightExists) {
    // 双页合上/封底时，centerContainer 会把唯一可见页移到逻辑跨页中线右侧。
    offsetX = pageWidth / 2;
  }

  return {
    width: pageWidth * (singlePageVisible ? 1 : 2),
    height: pageHeight,
    offsetX,
    offsetY: 0
  };
}

/**
 * 松手后按“四角对应四角”回弹。
 * 先在可见内容坐标系下判断四角是否进入 stage，再还原为 root.position。
 */
export function snapBackPosition(params: {
  position: Point;
  contentW: number;
  contentH: number;
  scale: number;
  viewportW: number;
  viewportH: number;
  contentOffsetX?: number;
  contentOffsetY?: number;
}): Point {
  const {
    position,
    contentW,
    contentH,
    scale,
    viewportW,
    viewportH,
    contentOffsetX = 0,
    contentOffsetY = 0
  } = params;

  const ox = contentOffsetX * scale;
  const oy = contentOffsetY * scale;
  const visualX = position.x + ox;
  const visualY = position.y + oy;
  const bookW = contentW * scale;
  const bookH = contentH * scale;
  const stage = {
    left: -viewportW / 2,
    top: -viewportH / 2,
    right: viewportW / 2,
    bottom: viewportH / 2
  };
  const book = {
    left: visualX,
    top: visualY,
    right: visualX + bookW,
    bottom: visualY + bookH
  };
  const target = snapByRectIntersection(book, stage);

  return {
    x: target.x - ox,
    y: target.y - oy
  };
}
